import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Clock, Loader2, ArrowUpDown, Repeat } from "lucide-react";
import { useAppSelector } from "@/store/hooks";
import { useTransactionQuote } from "@/hooks/useTransactionQuote";
import { normalizeCurrency } from "@/lib/utils";
import { formatCurrency } from "@/utils/formatCurrency";
import type { WalletAsset } from "@/interfaces/asset";
import SelectedAssetCard from "./SelectedAssetCard";
import AssetSelector from "./AssetSelector";
import type { SystemWalletType, Wallet } from "@/interfaces/wallet";
import TransactionSuccessDialog from "../TransactionSuccessDialog";
import { UserVerificationDialog } from "@/components/user-component/UserVerificationDialog";
import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import { toast } from "sonner";
import SwapQuoteCard from "./SwapQuoteCard";
import { getSpendableBalance } from "./helpers";

interface SwapPayload {
  networkId: string;
  fromAssetId: string;
  toAssetId: string;
  amount: string;
  systemType: SystemWalletType;
  securityCode: string;
}

interface Props {
  wallet: Wallet;
  assets: WalletAsset[];
  initialAsset?: WalletAsset;
  isLoading: boolean;
  onSwap: (payload: SwapPayload) => Promise<void>;
}

interface SwapState {
  from?: WalletAsset;
  to?: WalletAsset;
  amount: string;
}

const SwapContainer = ({
  wallet,
  assets,
  initialAsset,
  isLoading,
  onSwap,
}: Props) => {
  const { theme } = useAppSelector((state) => state.ui);

  const [swap, setSwap] = useState<SwapState>({
    from: initialAsset,
    to: undefined,
    amount: "",
  });

  const [verificationOpen, setVerificationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const [successData, setSuccessData] = useState<{
    from: string;
    to: string;
  } | null>(null);

  const QUOTE_VALIDITY = 30;
  const QUOTE_LIFETIME = 30;

  const [quoteExpiresIn, setQuoteExpiresIn] = useState(QUOTE_VALIDITY);

  const [secondsLeft, setSecondsLeft] = useState(QUOTE_LIFETIME);

  const quoteTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshingQuoteRef = useRef(false);

  const fromAsset = swap.from;
  const toAsset = swap.to;
  const amount = swap.amount;

  const fromAssets = useMemo(() => {
    return assets.filter((asset) => getSpendableBalance(asset) > 0);
  }, [assets]);

  const reverseAssets = () => {
    if (!swap.from || !swap.to) return;

    setSwap((prev) => ({
      ...prev,
      from: prev.to,
      to: prev.from,
    }));
  };

  useEffect(() => {
    if (swap.from || fromAssets.length === 0) return;

    setSwap((prev) => ({
      ...prev,
      from: fromAssets[0],
    }));
  }, [fromAssets, swap.from]);

  useEffect(() => {
    if (!swap.from || !swap.to) return;

    if (swap.from.id !== swap.to.id) return;

    setSwap((prev) => ({
      ...prev,
      to: undefined,
    }));
  }, [swap.from, swap.to]);

  useEffect(() => {
    if (!swap.from) return;

    if (swap.to) return;

    const firstDestination = assets.find(
      (asset) =>
        asset.id !== swap.from?.id &&
        normalizeCurrency(asset.currency ?? "") !==
          normalizeCurrency(swap.from?.currency ?? ""),
    );

    if (!firstDestination) return;

    setSwap((prev) => ({
      ...prev,
      to: firstDestination,
    }));
  }, [assets, swap.from, swap.to]);

  const swapAmount = useMemo(() => {
    const amt = Number(swap.amount);

    return Number.isFinite(amt) ? amt : 0;
  }, [swap.amount]);

  const fromBalance = useMemo(() => {
    return getSpendableBalance(swap.from);
  }, [swap.from]);

  const isInsufficient = useMemo(() => {
    if (!swapAmount || swapAmount <= 0) return false;

    return swapAmount > fromBalance;
  }, [swapAmount, fromBalance]);

  const isInvalidAmount = swapAmount <= 0;

  const quoteInput = useMemo(() => {
    if (!swapAmount || !swap.from || !swap.to) {
      return null;
    }

    if (isInvalidAmount || isInsufficient) {
      return null;
    }

    const fromCurrency = normalizeCurrency(swap.from.currency ?? "");

    const toCurrency = normalizeCurrency(swap.to.currency ?? "");

    if (!fromCurrency || !toCurrency) {
      return null;
    }

    return {
      domain: "swap" as const,
      amount: swapAmount,
      assetId: swap.from.id,
      fromCurrency,
      toCurrency,
    };
  }, [swapAmount, swap.from, swap.to, isInvalidAmount, isInsufficient]);

  const {
    quote,
    loading,
    refetch: refetchQuote,
  } = useTransactionQuote(quoteInput, !!quoteInput);

  /**
   * CENTRALIZED QUOTE REFRESH
   *
   * Both manual refresh and automatic expiry
   * use this function.
   */
  const handleRefreshQuote = useCallback(async () => {
    if (!quoteInput) return;

    // Prevent duplicate simultaneous refresh requests.
    if (refreshingQuoteRef.current) return;

    try {
      refreshingQuoteRef.current = true;

      const result = await refetchQuote();

      /**
       * React Query returns the fresh query result.
       * Only restart the timers after a successful result.
       */
      if (result?.data) {
        setSecondsLeft(QUOTE_LIFETIME);
        setQuoteExpiresIn(QUOTE_VALIDITY);
      }
    } catch (error) {
      console.error("Quote refresh failed:", error);

      toast.error("Unable to refresh quote. Please try again.");
    } finally {
      refreshingQuoteRef.current = false;
    }
  }, [quoteInput, refetchQuote]);

  /**
   * Whenever a new quote arrives, restart the
   * quote countdowns.
   */
  useEffect(() => {
    if (!quote) return;

    setSecondsLeft(QUOTE_LIFETIME);
    setQuoteExpiresIn(QUOTE_VALIDITY);
  }, [quote]);

  /**
   * Quote refresh countdown.
   *
   * When it reaches zero, handleRefreshQuote()
   * obtains a new quote.
   */
  useEffect(() => {
    if (!quote) return;

    if (quoteTimer.current) {
      clearInterval(quoteTimer.current);
    }

    quoteTimer.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      if (quoteTimer.current) {
        clearInterval(quoteTimer.current);
        quoteTimer.current = null;
      }
    };
  }, [quote]);

  /**
   * Automatically refresh when the refresh timer
   * reaches zero.
   */
  useEffect(() => {
    if (secondsLeft !== 0) return;
    if (!quoteInput) return;

    handleRefreshQuote();
  }, [secondsLeft, quoteInput, handleRefreshQuote]);

  /**
   * Quote expiration countdown.
   */
  useEffect(() => {
    if (!quote) return;
    if (quoteExpiresIn <= 0) return;

    const interval = setInterval(() => {
      setQuoteExpiresIn((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [quote, quoteExpiresIn]);

  // console.log("Swap From", swap.from);
  const validationError = useMemo(() => {
    if (!swap.from) {
      return "Select the asset to swap from.";
    }

    if (!swap.to) {
      return "Select the destination asset.";
    }

    if (swap.from.id === swap.to.id) {
      return "Source and destination assets cannot be the same.";
    }

    if (!swap.amount.trim()) {
      return "Enter an amount.";
    }

    if (isInvalidAmount) {
      return "Enter a valid amount.";
    }

    if (isInsufficient) {
      return "Insufficient balance.";
    }

    if (!quote) {
      return swap?.to?.token?.code === "ECO"
        ? "Unable to calculate a quote. ECO is not listed yet. You can only buy, hold, or transfer to BuyFiat users or external wallets."
        : "Unable to calculate a quote.";
    }

    if (quoteExpiresIn <= 0) {
      return "Quote expired.";
    }

    return null;
  }, [swap, quote, quoteExpiresIn, isInvalidAmount, isInsufficient]);

  const canExecuteSwap =
    !validationError && !loading && !isLoading && secondsLeft > 0;

  const updateFromAsset = (asset: WalletAsset) => {
    setSwap((prev) => ({
      ...prev,
      from: asset,
    }));
  };

  const updateToAsset = (asset: WalletAsset) => {
    setSwap((prev) => ({
      ...prev,
      to: asset,
    }));
  };

  const updateAmount = (value: string) => {
    setSwap((prev) => ({
      ...prev,
      amount: value,
    }));
  };

  const executeSwap = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    if (!swap.from || !swap.to) return;

    try {
      await onSwap({
        networkId: swap.from.token.blockchainNetworkId,
        fromAssetId: swap.from.id,
        toAssetId: swap.to.id,
        amount: swap.amount,
        systemType: wallet.systemType,
        securityCode,
      });

      setSuccessData({
        from: swap.from.token.symbol,
        to: swap.to.token.symbol,
      });

      setSuccessOpen(true);

      setSwap((prev) => ({
        ...prev,
        amount: "",
        to: undefined,
      }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? "Swap failed");
    }
  };

  return (
    <div data-lenis-prevent className="w-full bg-black/50 overflow-y-auto">
      <div className="w-full p-2 sm:p-4 md:p-6">
        <div
          data-lenis-prevent
          className={`rounded-xl lg:max-w-4xl mx-auto w-full max-h-[90vh] overflow-y-auto ${
            theme === "dark" ? "bg-card" : "bg-white"
          }`}
        >
          {/* HEADER */}
          <div
            className={`flex items-center justify-between p-6 border-b ${
              theme === "dark"
                ? "border-border bg-card"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F0B90B]/20 flex items-center justify-center">
                <Repeat className="w-5 h-5 text-primary" />
              </div>

              <div>
                <h3
                  className={`text-lg font-semibold ${
                    theme === "dark" ? "text-foreground" : "text-gray-900"
                  }`}
                >
                  Swap Assets
                </h3>

                <p
                  className={`text-sm ${
                    theme === "dark" ? "text-muted-foreground" : "text-gray-500"
                  }`}
                >
                  Convert between wallet assets
                </p>
              </div>
            </div>
          </div>

          {/* BODY */}
          <div className="p-2 sm:p-4 space-y-5">
            {/* FROM */}
            <div>
              <label className="text-sm text-muted-foreground">From</label>

              <SelectedAssetCard asset={swap.from} />

              <AssetSelector
                assets={fromAssets}
                value={swap.from}
                onChange={updateFromAsset}
                label="Swap From"
                placeholder="Search source asset..."
                excludeIds={swap.to ? [swap.to.id] : []}
              />

              <div className="mt-2 text-xs text-muted-foreground flex justify-between">
                <span>Available Balance</span>

                <span
                  className={
                    isInsufficient
                      ? "text-red-500 font-medium"
                      : "text-foreground"
                  }
                >
                  <span className="flex items-center gap-1 text-emerald-500 text-lg">
                    {formatCurrency(getSpendableBalance(swap.from))}{" "}
                    {normalizeCurrency(swap.from?.currency ?? "")}
                  </span>
                </span>
              </div>

              {isInsufficient && (
                <p className="text-xs text-red-500 mt-1">
                  Insufficient balance for this swap
                </p>
              )}
            </div>

            {/* REVERSE */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={reverseAssets}
                disabled={!swap.from || !swap.to}
                className={`rounded-full p-3 border transition-all ${
                  theme === "dark"
                    ? "bg-muted border-border hover:bg-primary/10"
                    : "bg-white border-gray-200 hover:bg-gray-100"
                } disabled:opacity-50`}
              >
                <ArrowUpDown className="w-5 h-5 text-primary" />
              </button>
            </div>

            {/* TO */}
            <div>
              <label className="text-sm text-muted-foreground">To</label>

              <SelectedAssetCard asset={swap.to} />

              <AssetSelector
                assets={assets.filter(
                  (asset) =>
                    asset.id !== swap.from?.id &&
                    normalizeCurrency(asset.currency ?? "") !==
                      normalizeCurrency(swap.from?.currency ?? ""),
                )}
                value={swap.to}
                label="Swap To"
                placeholder="Search destination asset..."
                excludeIds={swap.from ? [swap.from.id] : []}
                onChange={updateToAsset}
              />
            </div>

            {/* AMOUNT */}
            <div>
              <label className="text-sm text-muted-foreground">Amount</label>

              <div className="relative">
                <input
                  value={swap.amount}
                  onChange={(e) => updateAmount(e.target.value)}
                  placeholder="0.00"
                  className={`w-full px-4 py-3 pr-20 border rounded-lg h-14 ${
                    theme === "dark"
                      ? "bg-muted border-border text-foreground"
                      : "bg-white border-gray-200"
                  }`}
                />

                <button
                  type="button"
                  onClick={() =>
                    updateAmount(
                      Number(fromBalance).toFixed(
                        (fromAsset?.token?.decimals ?? 18) > 6
                          ? 6
                          : (fromAsset?.token?.decimals ?? 2),
                      ),
                    )
                  }
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded bg-primary text-black text-xs font-semibold"
                >
                  MAX
                </button>
              </div>

              {isInvalidAmount && swap.amount && (
                <p className="text-xs text-red-500 mt-1">
                  Enter a valid amount
                </p>
              )}
            </div>

            {/* LOADING */}
            {loading && (
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Calculating fee and rate...
              </p>
            )}

            {/* INFO */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F0B90B]/10">
              <Clock className="w-5 h-5 text-primary mt-0.5" />

              <p className="text-sm text-primary">
                Swap rates are dynamically calculated at execution time.
              </p>
            </div>

            {/* QUOTE */}
            <SwapQuoteCard
              loading={loading}
              quote={quote}
              amount={swapAmount}
              fromCurrency={swap.from?.currency ?? ""}
              toCurrency={swap.to?.currency ?? ""}
              quoteExpiresIn={quoteExpiresIn}
              secondsLeft={secondsLeft}
              refetchQuote={handleRefreshQuote}
              toAsset={toAsset}
            />

            {/* VALIDATION */}
            {validationError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                <p className="text-sm text-red-500">{validationError}</p>
              </div>
            )}

            {/* SUBMIT */}
            <button
              onClick={() => {
                if (!canExecuteSwap) return;

                setVerificationOpen(true);
              }}
              disabled={!canExecuteSwap}
              className={`w-full py-4 rounded-lg font-semibold transition-all ${
                canExecuteSwap
                  ? "bg-[#F0B90B] text-black hover:brightness-110"
                  : "bg-[#F0B90B] text-black cursor-not-allowed disabled:opacity-50"
              }`}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : validationError ? (
                validationError
              ) : (
                "Execute Swap"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* VERIFICATION */}
      {wallet?.isSystem ? (
        <AdminVerificationDialog
          open={verificationOpen}
          onOpenChange={setVerificationOpen}
          systemType={wallet.systemType}
          onSuccess={executeSwap}
          isSwap={true}
        />
      ) : (
        <UserVerificationDialog
          open={verificationOpen}
          onOpenChange={setVerificationOpen}
          loading={loading}
          onVerify={executeSwap}
          isSwap={true}
        />
      )}

      {/* SUCCESS — ONLY ONE */}
      <TransactionSuccessDialog
        open={successOpen}
        onOpenChange={setSuccessOpen}
        title="Swap Successful"
        message={
          successData
            ? `Successfully swapped ${successData.from} to ${successData.to}.`
            : "Your assets have been swapped successfully."
        }
      />
    </div>
  );
};

export default SwapContainer;
